import { GUEST_POSTS_TENANT_IDS } from '../auth/guestAuth';
import { invalidateDiscussionPreferencesCache } from '../cache/discussionPreferencesCache';
import { clearDiscussionMediaMemoryCache } from '../cache/discussionMediaCache';

const ACTIVE_TENANT_KEY = 'od_discussion_active_tenant_v1';

const LEGACY_UNSCOPED_PREFIXES = [
  'od_discussion_feed_v1:',
  'od_discussion_home_summary_v1',
  'od_discussion_thread_v1:',
];

/** Tenant id used for cache keys (profile wins; guests use configured public tenant). */
export function resolveDiscussionCacheTenantId(profileTenantId) {
  const fromProfile = String(profileTenantId || '').trim();
  if (fromProfile) return fromProfile;
  return String(GUEST_POSTS_TENANT_IDS[0] || '').trim();
}

export function getActiveDiscussionTenantId() {
  if (typeof window === 'undefined') return '';
  try {
    return String(sessionStorage.getItem(ACTIVE_TENANT_KEY) || '').trim();
  } catch {
    return '';
  }
}

function removeSessionKeysMatching(predicate) {
  if (typeof window === 'undefined') return;
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
      const key = sessionStorage.key(i);
      if (key && predicate(key)) sessionStorage.removeItem(key);
    }
  } catch {
    /* quota / private mode */
  }
}

/** Drop pre-tenant-scoping caches (one-time migration / safety). */
export function purgeLegacyUnscopedDiscussionCaches() {
  removeSessionKeysMatching((key) => {
    if (key === ACTIVE_TENANT_KEY) return false;
    return LEGACY_UNSCOPED_PREFIXES.some((prefix) => {
      if (prefix.endsWith(':')) return key.startsWith(prefix) && key.indexOf(':', prefix.length) === -1;
      return key === prefix || key.startsWith(`${prefix}:`);
    });
  });
}

export function clearDiscussionCachesForTenant(tenantId) {
  const tid = String(tenantId || '').trim();
  if (!tid) return;
  const scopedPrefixes = [
    `od_discussion_feed_v1:${tid}:`,
    `od_discussion_home_summary_v1:${tid}`,
    `od_discussion_thread_v1:${tid}:`,
  ];
  removeSessionKeysMatching((key) => scopedPrefixes.some((p) => key.startsWith(p) || key === p));
}

export function clearAllDiscussionClientCaches() {
  purgeLegacyUnscopedDiscussionCaches();
  const active = getActiveDiscussionTenantId();
  if (active) clearDiscussionCachesForTenant(active);
  try {
    sessionStorage.removeItem(ACTIVE_TENANT_KEY);
  } catch {
    /* ignore */
  }
  clearDiscussionMediaMemoryCache();
  invalidateDiscussionPreferencesCache();
}

/**
 * Call when profile tenant is known. Clears stale tenant caches on switch.
 * @returns {{ changed: boolean, tenantId: string }}
 */
export function syncDiscussionTenantScope(profileTenantId) {
  const next = resolveDiscussionCacheTenantId(profileTenantId);
  if (!next || typeof window === 'undefined') {
    return { changed: false, tenantId: next };
  }

  purgeLegacyUnscopedDiscussionCaches();

  const prev = getActiveDiscussionTenantId();
  const changed = Boolean(prev && prev !== next);
  if (changed) {
    clearDiscussionCachesForTenant(prev);
    clearDiscussionMediaMemoryCache();
    invalidateDiscussionPreferencesCache();
  }

  try {
    sessionStorage.setItem(ACTIVE_TENANT_KEY, next);
  } catch {
    /* ignore */
  }

  return { changed, tenantId: next };
}

/** Returns tenant id for cache read/write; null if scope not ready. */
export function requireDiscussionCacheTenantId(profileTenantId) {
  const tid = resolveDiscussionCacheTenantId(profileTenantId || getActiveDiscussionTenantId());
  return tid || null;
}
